<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import StatusTag from "~/components/Common/StatusTag.vue";
import { useBookings } from "~/composables/bookings/useBookings";
import { useEmployeeTrainings } from "~/composables/employee-trainings/useEmployeeTrainings";
import { useFormats } from "~/composables/utils/useFormat";
import { CopilotResourceKindEnum } from "~/utils/enums/copilot/chat.enums";
import { BookingStatusEnum } from "~/utils/enums/bookings-events.enums";
import { ColorsTypeEnum } from "~/utils/enums/colores.enums";
import { RequestStatus } from "~/utils/enums/request.enums";
import { TrainingPlanStatusEnum } from "~/utils/enums/training-plan-enums";
import { copilotResourceRows } from "~/utils/helpers/copilot/resources";
import type {
  ICopilotResourceCard,
  ICopilotResourceRow,
  ICopilotResources,
} from "~/utils/interfaces/copilot-interfaces";

const props = defineProps<{
  resources: ICopilotResources;
}>();

const { t } = useI18n();
const formats = useFormats();
const { statusMapping: bookingStatusMapping } = useBookings();
const { statusMapping: trainingStatusMapping } = useEmployeeTrainings();

const REQUEST_STATUS_VALUES = new Set<string>([
  RequestStatus.WAITING_MANAGER_INPUT,
  RequestStatus.WAITING_PRIORITY_CONFIRMATION,
  RequestStatus.WAITING_QUALIFICATION,
  RequestStatus.WAITING_HR_VALIDATION,
  RequestStatus.REQUEST_REFUSED,
  RequestStatus.REQUEST_CANCELED,
  RequestStatus.VALIDATED,
  RequestStatus.NO_VALIDATION_NEEDED,
  RequestStatus.WAITING_FINANCING_VALIDATION,
  RequestStatus.PLANNED,
  RequestStatus.ON_HOLD,
]);
const BOOKING_STATUS_VALUES = new Set<string>(Object.values(BookingStatusEnum));
const TRAINING_STATUS_VALUES = new Set<string>([
  ...REQUEST_STATUS_VALUES,
  TrainingPlanStatusEnum.PENDING_PROVIDER,
  TrainingPlanStatusEnum.NO_SHOW,
  TrainingPlanStatusEnum.PENDING_EMPLOYEE,
  TrainingPlanStatusEnum.REGISTERED,
  TrainingPlanStatusEnum.IN_PROGRESS,
  TrainingPlanStatusEnum.COMPLETED,
  TrainingPlanStatusEnum.TO_COMPLETE,
  TrainingPlanStatusEnum.FINISHED,
]);

/**
 * Unmapped statuses keep their raw value on a neutral tag rather than disappearing. Stays in the
 * block: the mappings come from store-backed composables the row derivation must not depend on.
 */
function statusTagOf(resource: ICopilotResourceCard): ICopilotResourceRow["statusTag"] {
  if (!resource.status) return null;

  if (resource.kind === CopilotResourceKindEnum.COMPANY_REGISTRATION && BOOKING_STATUS_VALUES.has(resource.status)) {
    const mapped = bookingStatusMapping(resource.status);
    if (mapped) return { value: mapped.value, type: mapped.type };
  }

  if (
    (resource.kind === CopilotResourceKindEnum.EMPLOYEE_REQUEST ||
      resource.kind === CopilotResourceKindEnum.COMPANY_REQUEST) &&
    REQUEST_STATUS_VALUES.has(resource.status)
  ) {
    const mapped = trainingStatusMapping(resource.status);
    return { value: mapped.value, type: mapped.type };
  }

  if (
    (resource.kind === CopilotResourceKindEnum.TRAINING_PLAN_SESSION ||
      resource.kind === CopilotResourceKindEnum.EMPLOYEE_TRAINING) &&
    (TRAINING_STATUS_VALUES.has(resource.status) || REQUEST_STATUS_VALUES.has(resource.status))
  ) {
    const mapped = trainingStatusMapping(resource.status, true);
    return { value: mapped.value, type: mapped.type };
  }

  return { value: resource.status, type: ColorsTypeEnum.LIGHT_GREY };
}

/** Keep every valid backend item in stream order; malformed future kinds render nothing. */
const links = computed(() =>
  copilotResourceRows(props.resources, { t, formatDate: formats.toDateDisplay, statusTagOf }),
);
</script>

<template>
  <div v-if="links.length" class="flex w-full flex-col gap-2">
    <a
      v-for="(link, index) in links"
      :key="`${link.href}-${index}`"
      class="flex items-center gap-3 rounded-lg border border-stroke-color bg-light-color px-4 py-3 hover:border-primary-blue-color"
      :href="link.href"
      :aria-label="`${link.resourceLabel} : ${link.title}`"
      target="_blank"
      rel="noopener noreferrer"
    >
      <div class="flex min-w-0 flex-1 flex-col gap-0.5">
        <!-- The provider is capped so a long organisation name cannot eat the title. -->
        <div class="flex min-w-0 items-center gap-1.5">
          <SvgIcon :name="link.icon" class="h-4 w-4 shrink-0 text-secondary-color" />
          <span class="body_med min-w-0 truncate text-primary-color">{{ link.title }}</span>
          <template v-if="link.providerName">
            <span class="body_regular shrink-0 text-secondary-color">·</span>
            <span class="body_regular max-w-[35%] shrink-0 truncate text-secondary-color">
              {{ link.providerName }}
            </span>
          </template>
        </div>

        <div class="flex min-w-0 flex-wrap items-center gap-x-3.5 gap-y-1 text-secondary-color">
          <span v-if="link.dates" class="flex shrink-0 items-center gap-[5px]">
            <SvgIcon name="date" class="h-4 w-4 shrink-0" />
            <span class="meta">{{ link.dates }}</span>
          </span>
          <span v-if="link.location" class="flex min-w-0 max-w-full items-center gap-[5px]">
            <SvgIcon :name="link.locationIcon" class="h-4 w-4 shrink-0" />
            <span class="meta_regular truncate">{{ link.location }}</span>
          </span>
          <span v-if="link.attendeeName" class="flex min-w-0 max-w-full items-center gap-[5px]">
            <SvgIcon name="indiv" class="h-4 w-4 shrink-0" />
            <span class="meta_regular truncate">{{ link.attendeeName }}</span>
          </span>
        </div>
      </div>

      <StatusTag v-if="link.statusTag" :tag="link.statusTag" class="shrink-0" />
      <SvgIcon name="link" class="h-4 w-4 shrink-0 text-secondary-color" />
    </a>
  </div>
</template>
